import { CSSProperties, forwardRef, useRef, useState } from "react";
import { dateMatchModifiers, type DateRange, type Matcher } from "react-day-picker";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { utc } from "@date-fns/utc";
import styled from "@emotion/styled";
import { format, isValid, parse, parseISO, parseJSON } from "date-fns";
import { Flex, Text } from "styles/utility-components";

import UIcon from "lib/icons/untitled_ui/UIcon";
import Button from "../button/Button";
import ButtonSplit from "../button_split/ButtonSplit";
import Calendar, { CalendarProps } from "../calendar/Calendar";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "../input_group/InputGroup";
import Popover from "../popover/Popover";
import { combineRefs } from "./util";

export type DateInputProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  React.ComponentPropsWithoutRef<"input">,
  "placeholder" | "onChange"
> & {
  calendarProps: Omit<
    CalendarProps,
    "month" | "onMonthChange" | "selected" | "onSelect" | "startMonth" | "endMonth"
  >;

  name: Path<TFieldValues>;
  control: Control<TFieldValues>; // Control isn't optional due to difficulty managing the calendar state without it

  // Limits the input and calendar to showing/selecting dates from this range
  minimumDate?: Date;
  maximumDate?: Date;

  triggerWidth?: CSSProperties["width"];
};

type FieldOutputValue = Date | Date[] | DateRange | null | undefined;
type FieldInputValue = FieldOutputValue | string | string[];

const dateFormat = "dd/MM/yyyy";

const plainDateFormatter = (date: FieldInputValue): string => {
  if (date === undefined || date === null) return "";
  if (typeof date === "string") {
    const parsed = parseJSON(date, { in: utc });
    if (isValid(parsed)) {
      return format(parsed, dateFormat, { in: utc });
    }
    throw new Error(`Invalid date string: ${date}`);
  }
  if (date instanceof Date) {
    return format(date, dateFormat, { in: utc });
  }

  if (Array.isArray(date)) {
    if (date.length === 0) return "";
    if (date.length === 1) {
      if (date[0] instanceof Date) {
        return format(date[0], dateFormat, { in: utc });
      }
      const parsed = parseJSON(date[0], { in: utc });
      if (isValid(parsed)) {
        return format(parsed, dateFormat, { in: utc });
      }
      throw new Error(`Invalid date string in array: ${date[0]}`);
    }
    return "Multiple dates";
  }

  if ("from" in date || "to" in date) {
    const { from, to } = date;
    const result = [];
    if (from) result.push(format(from, dateFormat, { in: utc }));
    if (to) result.push(format(to, dateFormat, { in: utc }));
    return result.join(" - ");
  }
  return "";
};

const parseDateFromUser = (input: string): Date | null => {
  // Preferred format
  const parsedWanted = parse(input, dateFormat, new Date(), { in: utc });
  if (isValid(parsedWanted)) {
    return parsedWanted;
  }
  // Allow dashes instead of slashes since it's a common mistake
  const parsedDashes = parse(input, dateFormat.replace(/\//g, "-"), new Date(), { in: utc });
  if (isValid(parsedDashes)) {
    return parsedDashes;
  }
  // Check if it's an ISO string
  const parseIso = parseISO(input, { in: utc });
  if (isValid(parseIso)) {
    return parseIso;
  }
  return null;
};

// Exported for testing
export const onChangeTextInput = (
  e: React.ChangeEvent<HTMLInputElement>,
  inputValue: string | null,
  setInputValue: (value: string) => void,
  setMonth: (month: Date) => void,
  fieldOnChange: (value: FieldOutputValue) => void,
  disabledDates: Matcher[],
) => {
  // If anything but numbers, slashes or dashes are input, ignore it
  if (/[^0-9/-]/.test(e.target.value)) {
    return;
  }

  // If the input is cleared, clear the value and return early
  if (e.target.value === "") {
    setInputValue(e.target.value);
    fieldOnChange(null);
    e.target.setCustomValidity("");
    return;
  }

  // Don't add separator if last character is already a separator to avoid // or --
  if (
    !(e.target.value.endsWith("/") && inputValue?.endsWith("/")) &&
    !(e.target.value.endsWith("-") && inputValue?.endsWith("-"))
  ) {
    setInputValue(e.target.value);
  }

  // If both separators are used, prefer the last one entered and remove the other ones. This is so it is easier to
  // enter an ISO formatted date e.g. typing 2024 shows as 20/24 but adding the dash it changes to 2024-
  if (
    e.target.value.includes("/") &&
    e.target.value.includes("-") &&
    (e.target.value.endsWith("/") || e.target.value.endsWith("-"))
  ) {
    const newSeparator = e.target.value.endsWith("/") ? "/" : "-";
    const newValue = e.target.value.replaceAll(newSeparator === "/" ? "-" : "/", "");
    setInputValue(newValue);
  }

  // Auto add slashes for user convenience but don't on backspace
  // Not running if dashes have been used since we can parse both formats and it would interfere with the user input
  if (!e.target.value.includes("-")) {
    const dateChunks = e.target.value.split("/");
    const lastChunk = dateChunks.slice(-1)[0];
    if (lastChunk.length === 2 && dateChunks.length <= 2) {
      const slashAdded = e.target.value + "/";
      // This check is to ensure backspace works as expected
      if (slashAdded !== inputValue) {
        setInputValue(slashAdded);
      }
    }
  }

  const parsed = parseDateFromUser(e.target.value);
  if (parsed) {
    if (dateMatchModifiers(parsed, disabledDates)) {
      e.target.setCustomValidity("Specified date is not allowed");
    } else {
      e.target.setCustomValidity("");
      fieldOnChange(parsed);
      setMonth(parsed);
    }
  } else {
    e.target.setCustomValidity(
      "Invalid date format. Expected dd/mm/yyyy, dd-mm-yyyy or yyyy-mm-dd",
    );
  }
};

const getCurrentMonth = (month: Date | null, fieldValue: FieldInputValue): Date => {
  if (month !== null) return month;
  if (fieldValue === undefined || fieldValue === null) return new Date();

  if (typeof fieldValue === "string") {
    const parsed = parseJSON(fieldValue, { in: utc });
    if (isValid(parsed)) {
      return parsed;
    }
    return new Date();
  }
  if (fieldValue instanceof Date) return fieldValue;

  if (Array.isArray(fieldValue) && fieldValue.length > 0) {
    if (fieldValue[0] instanceof Date) {
      return fieldValue[0];
    }
    const parsed = parseJSON(fieldValue[0], { in: utc });
    if (isValid(parsed)) {
      return parsed;
    }
    return new Date();
  }

  if ("from" in fieldValue && fieldValue.from) return fieldValue.from;
  if ("to" in fieldValue && fieldValue.to) return fieldValue.to;
  return new Date();
};

const DateInput = forwardRef(function DateInput<TFieldValues extends FieldValues = FieldValues>(
  {
    control,
    name,
    disabled,
    calendarProps,
    minimumDate = new Date(2015, 0, 1),
    maximumDate = new Date(new Date().getFullYear() + 10, 11, 31),
    triggerWidth,
    ...props
  }: DateInputProps<TFieldValues>,
  externalRef: React.ForwardedRef<HTMLInputElement>,
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string | null>(null); // Null = use value from the controller
  const [month, setMonth] = useState<Date | null>(null); // Null = use currently selected month or current month if no date selected

  const disabledDates: Matcher[] = [
    ...(Array.isArray(calendarProps.disabled)
      ? calendarProps.disabled
      : calendarProps.disabled !== undefined
        ? [calendarProps.disabled]
        : []),
    { before: minimumDate },
    { after: maximumDate },
  ];

  return (
    <Controller
      name={name}
      control={control}
      disabled={disabled}
      render={({ field }) => {
        const clearInput = (e: React.MouseEvent) => {
          e.stopPropagation();
          field.onChange(null);
          setInputValue(null);
          inputRef.current?.setCustomValidity("");
          setMonth(new Date()); // Assumes the current date isn't disabled, will need updating if that becomes a use case
        };

        return (
          <Popover
            trigger={
              <ButtonSplit secondaryType="button" width={triggerWidth}>
                <Button
                  buttonType="primary"
                  buttonStyle="inverse"
                  fullWidth
                  justifyContent="left"
                  disabled={field.disabled}
                >
                  {plainDateFormatter(field.value) === "" ? (
                    <>
                      <UIcon icon="plus" color="sysTextPlaceholder" />
                      <Text color="sysTextPlaceholder">Date</Text>
                    </>
                  ) : (
                    plainDateFormatter(field.value)
                  )}
                </Button>
                <ClearButton
                  icon="xCircle"
                  buttonType="primary"
                  buttonStyle="inverse"
                  onClick={clearInput}
                  aria-label="Clear date"
                  disabled={field.disabled || !field.value}
                />
              </ButtonSplit>
            }
            open={isPopoverOpen && !field.disabled}
            onOpenChange={(open) => {
              if (field.disabled) return;
              setIsPopoverOpen(open);
            }}
            hideArrow
            borderRadius={8}
            contentPadding={16}
            align="start"
          >
            <Flex column gap={16}>
              <InputGroup>
                <InputGroupInput
                  {...props}
                  {...field}
                  type="text"
                  placeholder={
                    calendarProps.mode === "single"
                      ? dateFormat.toLowerCase()
                      : `Select ${calendarProps.mode === "range" ? "a date range" : "multiple dates"} below`
                  }
                  ref={combineRefs(field.ref, externalRef, inputRef)}
                  onChange={(e) =>
                    onChangeTextInput(
                      e,
                      inputValue,
                      setInputValue,
                      setMonth,
                      field.onChange,
                      disabledDates,
                    )
                  }
                  onBlur={(e) => {
                    if (e.target.checkValidity()) {
                      setInputValue(null);
                    } else {
                      e.target.reportValidity();
                    }
                    field.onBlur();
                    props.onBlur?.(e);
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter" && (e.target as HTMLInputElement)?.checkValidity()) {
                      e.preventDefault();
                      setInputValue(null);
                      setIsPopoverOpen(false);
                    }
                    props.onKeyDown?.(e);
                  }}
                  value={inputValue ?? plainDateFormatter(field.value)}
                  readOnly={calendarProps.mode === "range" || calendarProps.mode === "multiple"}
                  disabled={field.disabled}
                />
                <InputGroupAddon align="inline-end">
                  <ClearButtonGroup
                    icon="xCircle"
                    onClick={clearInput}
                    aria-label="Clear date"
                    disabled={field.disabled || !field.value}
                  />
                </InputGroupAddon>
              </InputGroup>
              <Calendar
                {...calendarProps}
                // Not spreading field since it mostly props won't apply to the calendar
                disabled={field.disabled || disabledDates}
                startMonth={minimumDate}
                endMonth={maximumDate}
                month={getCurrentMonth(month, field.value)}
                onMonthChange={setMonth}
                selected={field.value}
                onSelect={(value: FieldOutputValue) => {
                  field.onChange(value);
                  setInputValue(null);
                  inputRef.current?.setCustomValidity("");
                }}
                timeZone="UTC"
              />
            </Flex>
          </Popover>
        );
      }}
    />
  );
}) as <TFieldValues extends FieldValues = FieldValues>(
  props: DateInputProps<TFieldValues> & { ref?: React.ForwardedRef<HTMLInputElement> },
) => React.ReactElement;

const ClearButton = styled(Button)({
  "&:disabled": {
    display: "none",
  },
});
const ClearButtonGroup = styled(InputGroupButton)({
  "&:disabled": {
    display: "none",
  },
});

export default DateInput;
